import { useEffect, useRef, useState } from "react";
import {
  UploadSimple,
  FilePdf,
  FileCsv,
  FileXls,
  FileText,
  Trash,
  Sparkle,
  CheckCircle,
  ArrowClockwise,
  Warning,
  X,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtMoney, ACCOUNT_TYPE_LABELS, DEBT_TYPE_LABELS } from "../../lib/finance-format";

const EXT_ICON = {
  pdf: FilePdf,
  csv: FileCsv,
  xlsx: FileXls,
  xls: FileXls,
  txt: FileText,
};

const EXT_COLOR = {
  pdf: "#EF4444",
  csv: "#10B981",
  xlsx: "#16A34A",
  xls: "#16A34A",
  txt: "#94A3B8",
};

function StatusPill({ status }) {
  const map = {
    uploaded: { label: "SUBIDO", bg: "bg-blue-500/15", text: "text-blue-400" },
    parsed: { label: "ANALIZADO", bg: "bg-[#FACC15]/15", text: "text-[#FACC15]" },
    imported: { label: "IMPORTADO", bg: "bg-emerald-500/15", text: "text-emerald-400" },
    failed: { label: "ERROR", bg: "bg-red-500/15", text: "text-red-400" },
  };
  const cfg = map[status] || map.uploaded;
  return (
    <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

/**
 * Panel that lives INSIDE the Ingresos & Gastos tab.
 * Lets the user upload PDF/CSV/XLSX bank statements, parse them with the CFO AI,
 * and import the extracted accounts/debts into their workspace.
 */
export default function DocumentImporter({ scope, onImported }) {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/finance/documents");
      setDocuments(data);
    } catch (err) {
      /* silent */
    }
  };

  useEffect(() => { load(); }, []);

  const doUpload = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "csv", "xlsx", "xls", "txt"].includes(ext)) {
      toast.error(`Formato no soportado: .${ext}. Usa PDF, CSV, XLSX o XLS.`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Máximo 10MB por archivo");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("scope", scope === "business" ? "business" : "personal");
    try {
      const { data } = await api.post("/finance/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${data.original_filename}" subido`);
      setDocuments((prev) => [data, ...prev]);
      // Auto-parse right away
      handleParse(data.document_id, true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    for (const f of Array.from(files)) {
      await doUpload(f);
    }
    e.target.value = "";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (!files) return;
    for (const f of Array.from(files)) {
      await doUpload(f);
    }
  };

  const handleParse = async (docId, silent = false) => {
    setParsing(true);
    try {
      if (!silent) toast.loading("Analizando con Claude Sonnet 4.6...", { id: docId });
      const { data } = await api.post(`/finance/documents/${docId}/parse`);
      if (!silent) toast.success("Documento analizado", { id: docId });
      setDocuments((prev) =>
        prev.map((d) => (d.document_id === docId ? { ...d, status: "parsed", extracted: data.extracted } : d))
      );
      const fresh = { ...documents.find((d) => d.document_id === docId), status: "parsed", extracted: data.extracted };
      setReviewDoc(fresh);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo analizar", { id: docId });
    } finally {
      setParsing(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/finance/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.document_id !== docId));
      toast.success("Documento eliminado");
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const handleReview = (doc) => {
    if (!doc.extracted) {
      handleParse(doc.document_id);
    } else {
      setReviewDoc(doc);
    }
  };

  return (
    <section
      data-testid="document-importer"
      className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
            IMPORTADOR IA · CLAUDE SONNET 4.6
          </div>
          <h3 className="text-base md:text-lg font-heading font-bold text-white">
            Cargar estados bancarios
          </h3>
          <p className="text-[11px] font-mono text-white/50 mt-0.5">
            Sube PDF, CSV, XLSX o XLS — el CFO extrae cuentas, deudas y transacciones.
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div
        data-testid="doc-dropzone"
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative rounded-lg border-2 border-dashed p-6 md:p-8 cursor-pointer transition text-center ${
          dragActive
            ? "border-[#FACC15] bg-[#FACC15]/8"
            : "border-white/15 hover:border-white/25 bg-black/30 hover:bg-black/40"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept=".pdf,.csv,.txt,.xlsx,.xls,application/pdf,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          data-testid="doc-file-input"
          disabled={uploading}
        />
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-2xl bg-[#FACC15]/15 border border-[#FACC15]/25 flex items-center justify-center">
            {uploading ? (
              <ArrowClockwise size={22} weight="bold" className="text-[#FACC15] animate-spin" />
            ) : (
              <UploadSimple size={22} weight="bold" className="text-[#FACC15]" />
            )}
          </div>
          <div className="text-sm font-heading font-bold text-white">
            {uploading ? "Subiendo..." : "Arrastra tu archivo o haz click"}
          </div>
          <div className="text-[11px] font-mono text-white/50 flex flex-wrap items-center justify-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded bg-white/6">PDF</span>
            <span className="px-1.5 py-0.5 rounded bg-white/6">CSV</span>
            <span className="px-1.5 py-0.5 rounded bg-white/6">XLSX</span>
            <span className="px-1.5 py-0.5 rounded bg-white/6">XLS</span>
            <span className="text-white/40">· máx 10MB</span>
          </div>
        </div>
      </div>

      {/* Documents list */}
      {documents.length > 0 && (
        <div className="mt-4 space-y-2">
          {documents.map((d) => {
            const Icon = EXT_ICON[d.extension] || FileText;
            const color = EXT_COLOR[d.extension] || "#94A3B8";
            return (
              <div
                key={d.document_id}
                data-testid={`doc-row-${d.document_id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-[#0F1017] border border-white/6 hover:border-white/15 transition group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${color}22`, borderColor: `${color}55`, border: `1px solid ${color}55` }}
                >
                  <Icon size={18} weight="fill" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-heading font-bold text-white truncate">
                      {d.original_filename}
                    </div>
                    <StatusPill status={d.status} />
                  </div>
                  <div className="text-[11px] font-mono text-white/50">
                    {(d.size / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleDateString()}
                    {d.import_summary && (
                      <span className="text-emerald-400 ml-2">
                        · {d.import_summary.accounts} cuentas
                        {" · "}
                        {d.import_summary.debts} deudas
                        {" · "}
                        {d.import_summary.transactions ?? 0} transacciones
                        {d.import_summary.skipped_duplicates > 0 && (
                          <span className="text-yellow-500 ml-1">
                            · {d.import_summary.skipped_duplicates} duplicada(s)
                          </span>
                        )}
                        {d.import_summary.replaced_existing > 0 && (
                          <span className="text-red-400 ml-1">
                            · {d.import_summary.replaced_existing} reemplazada(s)
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {d.status !== "imported" && (
                    <button
                      data-testid={`doc-review-${d.document_id}`}
                      onClick={() => handleReview(d)}
                      disabled={parsing}
                      className="px-3 py-1.5 rounded-lg bg-[#FACC15] hover:bg-[#EAB308] disabled:opacity-40 text-black font-heading font-bold text-xs flex items-center gap-1.5 transition"
                    >
                      <Sparkle size={12} weight="fill" />
                      {d.status === "parsed" ? "Revisar" : "Analizar"}
                    </button>
                  )}
                  <button
                    data-testid={`doc-delete-${d.document_id}`}
                    onClick={() => handleDelete(d.document_id)}
                    className="w-8 h-8 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 flex items-center justify-center transition"
                    title="Eliminar"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Review modal */}
      {reviewDoc && (
        <ReviewDialog
          doc={reviewDoc}
          scope={scope === "business" ? "business" : "personal"}
          onClose={() => setReviewDoc(null)}
          onImported={async (summary) => {
            setDocuments((prev) =>
              prev.map((d) => (d.document_id === reviewDoc.document_id ? { ...d, status: "imported", import_summary: summary } : d))
            );
            setReviewDoc(null);
            onImported?.();
            // If the import created transactions, try to auto-attach any that
            // match existing receivables (Zelle from Sergio -> Sergio's dossier).
            if ((summary?.transactions ?? 0) > 0) {
              try {
                const { data } = await api.post("/finance/receivables/auto-attach-all");
                if (data.total_attached > 0) {
                  toast.success(
                    `${data.total_attached} pagos vinculados automáticamente a ${data.per_client.length} expediente(s)`,
                  );
                }
              } catch (_) { /* silent */ }
            }
          }}
        />
      )}
    </section>
  );
}

// ------------------------------------------------------------------
// Review dialog — shows the AI's extraction, lets user tick/untick rows
// and edit values before committing to fin_accounts / fin_debts.
// ------------------------------------------------------------------
function ReviewDialog({ doc, scope: initScope, onClose, onImported }) {
  const ex = doc.extracted || {};
  const [accounts, setAccounts] = useState(
    (ex.accounts || []).map((a) => ({ ...a, _selected: true }))
  );
  const [debts, setDebts] = useState(
    (ex.debts || []).map((d) => ({ ...d, _selected: true }))
  );
  const [transactions, setTransactions] = useState(
    (ex.transactions || []).map((t) => ({ ...t, _selected: true }))
  );
  const [scope, setScope] = useState(initScope);
  const [importing, setImporting] = useState(false);
  const [dedupeMode, setDedupeMode] = useState("skip_duplicates");
  const [dedupePreview, setDedupePreview] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);

  const toggleAcc = (idx) =>
    setAccounts((prev) => prev.map((a, i) => (i === idx ? { ...a, _selected: !a._selected } : a)));
  const setAccField = (idx, k, v) =>
    setAccounts((prev) => prev.map((a, i) => (i === idx ? { ...a, [k]: v } : a)));
  const toggleDebt = (idx) =>
    setDebts((prev) => prev.map((d, i) => (i === idx ? { ...d, _selected: !d._selected } : d)));
  const setDebtField = (idx, k, v) =>
    setDebts((prev) => prev.map((d, i) => (i === idx ? { ...d, [k]: v } : d)));

  const selectedAccounts = accounts.filter((a) => a._selected);
  const selectedDebts = debts.filter((d) => d._selected);
  const selectedTx = transactions.filter((t) => t._selected);
  const total = selectedAccounts.length + selectedDebts.length + selectedTx.length;

  // Compute dedupe preview whenever the selected transactions change
  useEffect(() => {
    if (!doc?.document_id || selectedTx.length === 0) {
      setDedupePreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setDedupeLoading(true);
      try {
        const payload = {
          scope,
          transactions: selectedTx.map(({ _selected, ...rest }) => rest),
        };
        const { data } = await api.post(
          `/finance/documents/${doc.document_id}/preview-dedupe`,
          payload,
        );
        if (!cancelled) setDedupePreview(data);
      } catch (err) {
        if (!cancelled) setDedupePreview(null);
      } finally {
        if (!cancelled) setDedupeLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [doc?.document_id, transactions.map((t) => t._selected).join(","), scope]);

  const handleImport = async () => {
    if (total === 0) {
      toast.error("Selecciona al menos una fila");
      return;
    }
    setImporting(true);
    try {
      const payload = {
        scope,
        dedupe_mode: dedupeMode,
        accounts: selectedAccounts.map(({ _selected, ...rest }) => ({
          ...rest,
          balance: Number(rest.balance || 0),
          apr: rest.apr !== null && rest.apr !== "" && rest.apr !== undefined ? Number(rest.apr) : null,
          credit_limit: rest.credit_limit !== null && rest.credit_limit !== "" && rest.credit_limit !== undefined ? Number(rest.credit_limit) : null,
        })),
        debts: selectedDebts.map(({ _selected, ...rest }) => ({
          ...rest,
          balance: Number(rest.balance || 0),
          apr: rest.apr !== null && rest.apr !== "" && rest.apr !== undefined ? Number(rest.apr) : null,
          minimum_payment: rest.minimum_payment !== null && rest.minimum_payment !== "" && rest.minimum_payment !== undefined ? Number(rest.minimum_payment) : null,
        })),
        transactions: selectedTx.map(({ _selected, ...rest }) => rest),
      };
      const { data } = await api.post(`/finance/documents/${doc.document_id}/import`, payload);
      const s = data.summary || {};
      const parts = [
        s.accounts && `${s.accounts} cuentas`,
        s.debts && `${s.debts} deudas`,
        s.transactions && `${s.transactions} tx nuevas`,
      ].filter(Boolean).join(" + ");
      const skipped = s.skipped_duplicates
        ? ` · ${s.skipped_duplicates} duplicada(s) omitidas`
        : "";
      const replaced = s.replaced_existing
        ? ` · ${s.replaced_existing} reemplazadas`
        : "";
      toast.success(`Importado: ${parts}${skipped}${replaced}`);
      onImported?.(s);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = "No se pudo importar";
      if (Array.isArray(detail)) {
        // Pydantic validation error
        const parts = detail.slice(0, 2).map((d) => {
          const loc = Array.isArray(d.loc) ? d.loc.slice(-2).join(".") : "";
          return `${loc}: ${d.msg}`;
        });
        msg = `No se pudo importar (${status}): ${parts.join(" · ")}`;
      } else if (typeof detail === "string") {
        msg = `No se pudo importar (${status}): ${detail}`;
      } else if (status) {
        msg = `No se pudo importar (${status})`;
      }
      toast.error(msg, { duration: 12000 });
      console.error("[DocumentImporter] import error", { status, detail, err });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      data-testid="doc-review-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        <div className="p-5 border-b border-white/8 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15] flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> Revisar extracción · Claude Sonnet 4.6
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5 truncate">
              {doc.original_filename}
            </div>
            <div className="text-[11px] font-mono text-white/50 mt-1">
              {ex.detected_type ? `Tipo: ${ex.detected_type}` : ""}
              {ex.institution ? ` · Institución: ${ex.institution}` : ""}
              {ex.period?.start ? ` · Periodo: ${ex.period.start} → ${ex.period.end || "?"}` : ""}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Scope selection */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/50 mb-2">
              Registrar como
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-black/40 border border-white/10">
              {["personal", "business"].map((s) => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-widest font-bold transition ${
                    scope === s ? "bg-white/10 text-white" : "text-white/40"
                  }`}
                >
                  {s === "personal" ? "Personal" : "Negocio"}
                </button>
              ))}
            </div>
          </div>

          {/* Accounts */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/60">
                CUENTAS DETECTADAS · {accounts.length}
              </div>
              {accounts.length > 0 && (
                <button
                  onClick={() => setAccounts((prev) => prev.map((a) => ({ ...a, _selected: !prev.every((p) => p._selected) })))}
                  className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white"
                >
                  Todo / Nada
                </button>
              )}
            </div>
            {accounts.length === 0 && (
              <div className="text-[11px] font-mono text-white/30 py-3 text-center">No se detectaron cuentas.</div>
            )}
            {accounts.map((a, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg mb-2 border transition ${
                  a._selected ? "border-[#2563EB]/40 bg-[#2563EB]/5" : "border-white/8 bg-black/20 opacity-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={a._selected}
                    onChange={() => toggleAcc(i)}
                    className="w-4 h-4 accent-[#2563EB]"
                    data-testid={`review-acc-check-${i}`}
                  />
                  <input
                    value={a.name || ""}
                    onChange={(e) => setAccField(i, "name", e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-sm font-heading font-bold text-white"
                  />
                  <select
                    value={a.account_type || "checking"}
                    onChange={(e) => setAccField(i, "account_type", e.target.value)}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] font-mono text-white"
                  >
                    {Object.entries(ACCOUNT_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v} className="bg-[#0F1017]">{l}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-white/40 font-mono text-sm">$</span>
                    <input
                      type="number"
                      value={a.balance ?? 0}
                      onChange={(e) => setAccField(i, "balance", e.target.value)}
                      className="w-28 bg-black/40 border border-white/10 rounded px-2 py-1 text-right text-sm font-mono font-bold text-white tabular-nums"
                    />
                  </div>
                </div>
                {a.institution && (
                  <div className="ml-7 mt-1 text-[10px] font-mono text-white/40">
                    {a.institution}
                    {a.apr ? ` · APR ${a.apr}%` : ""}
                    {a.credit_limit ? ` · límite ${fmtMoney(a.credit_limit)}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Debts */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/60 mb-3">
              DEUDAS DETECTADAS · {debts.length}
            </div>
            {debts.length === 0 && (
              <div className="text-[11px] font-mono text-white/30 py-3 text-center">No se detectaron deudas separadas.</div>
            )}
            {debts.map((d, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg mb-2 border transition ${
                  d._selected ? "border-red-500/40 bg-red-500/5" : "border-white/8 bg-black/20 opacity-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={d._selected}
                    onChange={() => toggleDebt(i)}
                    className="w-4 h-4 accent-red-500"
                    data-testid={`review-debt-check-${i}`}
                  />
                  <input
                    value={d.name || ""}
                    onChange={(e) => setDebtField(i, "name", e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-sm font-heading font-bold text-white"
                  />
                  <select
                    value={d.debt_type || "personal"}
                    onChange={(e) => setDebtField(i, "debt_type", e.target.value)}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] font-mono text-white"
                  >
                    {Object.entries(DEBT_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v} className="bg-[#0F1017]">{l}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-white/40 font-mono text-sm">$</span>
                    <input
                      type="number"
                      value={d.balance ?? 0}
                      onChange={(e) => setDebtField(i, "balance", e.target.value)}
                      className="w-28 bg-black/40 border border-white/10 rounded px-2 py-1 text-right text-sm font-mono font-bold text-white tabular-nums"
                    />
                  </div>
                </div>
                <div className="ml-7 mt-1 flex items-center gap-3 text-[10px] font-mono text-white/40">
                  <span>APR:</span>
                  <input
                    type="number"
                    step="0.01"
                    value={d.apr ?? ""}
                    onChange={(e) => setDebtField(i, "apr", e.target.value)}
                    className="w-16 bg-black/40 border border-white/10 rounded px-2 py-0.5 text-white"
                  />
                  <span>%</span>
                  <span>·  Min:</span>
                  <input
                    type="number"
                    step="0.01"
                    value={d.minimum_payment ?? ""}
                    onChange={(e) => setDebtField(i, "minimum_payment", e.target.value)}
                    className="w-20 bg-black/40 border border-white/10 rounded px-2 py-0.5 text-white"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Transactions (now importable, not just preview) */}
          {ex.transactions && ex.transactions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/60">
                  TRANSACCIONES DETECTADAS · {transactions.length}
                </div>
                <button
                  onClick={() =>
                    setTransactions((prev) => {
                      const all = prev.every((p) => p._selected);
                      return prev.map((p) => ({ ...p, _selected: !all }));
                    })
                  }
                  className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white"
                >
                  Todo / Nada
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto rounded-lg border border-white/6 divide-y divide-white/6">
                {transactions.map((t, i) => (
                  <label
                    key={i}
                    className={`flex items-center gap-3 px-3 py-1.5 text-[11px] font-mono cursor-pointer hover:bg-white/4 ${
                      !t._selected ? "opacity-40" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!t._selected}
                      onChange={() =>
                        setTransactions((prev) =>
                          prev.map((tx, idx) => (idx === i ? { ...tx, _selected: !tx._selected } : tx))
                        )
                      }
                      className="w-3.5 h-3.5 accent-[#FACC15]"
                      data-testid={`review-tx-check-${i}`}
                    />
                    <span className="text-white/40 tabular-nums shrink-0">{t.date}</span>
                    <span className="flex-1 truncate text-white/70">{t.description}</span>
                    <span className="text-[9px] uppercase tracking-widest text-white/40 shrink-0">
                      {t.category}
                    </span>
                    <span className={`tabular-nums font-bold shrink-0 ${t.amount >= 0 ? "text-emerald-400" : "text-white/80"}`}>
                      {t.amount >= 0 ? "+" : ""}{fmtMoney(t.amount)}
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] font-mono text-white/30 mt-1.5">
                Se guardarán en Transacciones. El CFO auto-categorizará y podrá detectar recurrentes.
              </div>
            </div>
          )}

          {/* CÓMO PROCESAR — dedupe mode picker */}
          {selectedTx.length > 0 && (
            <div data-testid="dedupe-picker" className="mt-4 border-t border-white/6 pt-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/50 mb-2">
                CÓMO PROCESAR
              </div>
              {dedupeLoading ? (
                <div className="py-4 text-center text-[11px] font-mono text-white/40">
                  <ArrowClockwise size={13} weight="bold" className="animate-spin inline mr-1" />
                  Analizando duplicados...
                </div>
              ) : dedupePreview ? (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <StatMini
                    testId="dedupe-total"
                    label="En el archivo"
                    value={dedupePreview.total}
                    color="#94A3B8"
                  />
                  <StatMini
                    testId="dedupe-new"
                    label="Nuevas"
                    value={dedupePreview.new}
                    color="#10B981"
                  />
                  <StatMini
                    testId="dedupe-duplicates"
                    label="Ya existen"
                    value={dedupePreview.duplicates}
                    color="#F59E0B"
                  />
                </div>
              ) : null}

              {dedupePreview && dedupePreview.min_date && (
                <div className="text-[10px] font-mono text-white/40 mb-2">
                  Rango detectado: <span className="text-white/70">{dedupePreview.min_date}</span>
                  {" → "}
                  <span className="text-white/70">{dedupePreview.max_date}</span>
                  {dedupePreview.existing_in_range > 0 && (
                    <> · <span className="text-white/70">{dedupePreview.existing_in_range}</span> transacción(es) existente(s) en ese rango</>
                  )}
                </div>
              )}

              <div className="space-y-2">
                {/* skip_duplicates (recommended) */}
                <label
                  data-testid="dedupe-mode-skip"
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition ${
                    dedupeMode === "skip_duplicates"
                      ? "bg-emerald-500/8 border-emerald-500/40"
                      : "bg-black/25 border-white/8 hover:border-white/20"
                  }`}
                >
                  <input
                    type="radio"
                    name="dedupe_mode"
                    value="skip_duplicates"
                    checked={dedupeMode === "skip_duplicates"}
                    onChange={() => setDedupeMode("skip_duplicates")}
                    className="mt-1 accent-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ArrowClockwise size={13} weight="bold" className="text-emerald-400" />
                      <div className="text-sm font-heading font-bold text-white">
                        Actualizar sólo lo nuevo
                      </div>
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/25 text-emerald-300 font-bold">
                        RECOMENDADO
                      </span>
                    </div>
                    <div className="text-[11px] font-mono text-white/60 mt-0.5 leading-relaxed">
                      Inserta sólo las <b className="text-emerald-300">{dedupePreview?.new ?? "…"}</b> transacciones nuevas.
                      Las que ya existen quedan intactas. <b className="text-white">El histórico no se toca.</b>
                    </div>
                  </div>
                </label>

                {/* replace_period */}
                <label
                  data-testid="dedupe-mode-replace"
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition ${
                    dedupeMode === "replace_period"
                      ? "bg-red-500/8 border-red-500/40"
                      : "bg-black/25 border-white/8 hover:border-white/20"
                  }`}
                >
                  <input
                    type="radio"
                    name="dedupe_mode"
                    value="replace_period"
                    checked={dedupeMode === "replace_period"}
                    onChange={() => setDedupeMode("replace_period")}
                    className="mt-1 accent-red-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Warning size={13} weight="fill" className="text-red-400" />
                      <div className="text-sm font-heading font-bold text-white">
                        Reemplazar el período
                      </div>
                    </div>
                    <div className="text-[11px] font-mono text-white/60 mt-0.5 leading-relaxed">
                      Borra las <b className="text-red-300">{dedupePreview?.existing_in_range ?? "…"}</b> transacciones existentes entre{" "}
                      <b className="text-white">{dedupePreview?.min_date || "—"}</b> y{" "}
                      <b className="text-white">{dedupePreview?.max_date || "—"}</b>, y las reemplaza con las del archivo.
                      Útil sólo si quieres rehacer ese rango desde cero.
                    </div>
                  </div>
                </label>

                {/* append (legacy) */}
                <label
                  data-testid="dedupe-mode-append"
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition ${
                    dedupeMode === "append"
                      ? "bg-white/6 border-white/20"
                      : "bg-black/25 border-white/8 hover:border-white/15"
                  }`}
                >
                  <input
                    type="radio"
                    name="dedupe_mode"
                    value="append"
                    checked={dedupeMode === "append"}
                    onChange={() => setDedupeMode("append")}
                    className="mt-1 accent-white"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-heading font-bold text-white">
                      Insertar todo (permitir duplicados)
                    </div>
                    <div className="text-[11px] font-mono text-white/60 mt-0.5 leading-relaxed">
                      Inserta las {dedupePreview?.total ?? "…"} filas sin revisar duplicados. Sólo si sabes lo que haces.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/8 flex items-center justify-between gap-3">
          <div className="text-[11px] font-mono text-white/50">
            <span className="text-white font-bold tabular-nums">{total}</span> filas seleccionadas
            {dedupePreview && selectedTx.length > 0 && dedupeMode === "skip_duplicates" && (
              <> · se insertarán <span className="text-emerald-300 font-bold">{dedupePreview.new}</span></>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-white/60 hover:text-white text-sm"
            >
              Cancelar
            </button>
            <button
              data-testid="doc-import-btn"
              onClick={handleImport}
              disabled={importing || total === 0}
              className="on-solid px-4 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-40 text-white font-heading font-bold text-sm flex items-center gap-2"
            >
              {importing ? (
                <ArrowClockwise size={13} weight="bold" className="animate-spin" />
              ) : (
                <CheckCircle size={13} weight="fill" />
              )}
              {importing ? "Importando..." : "Cargar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatMini({ testId, label, value, color }) {
  return (
    <div
      data-testid={testId}
      className="p-2 rounded-lg bg-black/40 border-l-2"
      style={{ borderLeftColor: color }}
    >
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
        {label}
      </div>
      <div className="text-lg font-mono font-bold tabular-nums" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
